import {
  safeLocalStorageGetItem,
  safeLocalStorageRemoveItem,
  safeLocalStorageSetItem,
} from '@/lib/safe-storage';
import { getOrCreateSessionId } from '@/lib/events/session-id';
import { isNativeApp } from '@/lib/utils/native-communication';
import { isPWA } from '@/framework/utils/site-utils';
import { ensureCartSession, getCartAgeSeconds } from '@/lib/cart-session';
import { ATTRIBUTION_STORAGE_KEY } from '@/lib/attribution-storage-keys';
import {
  ATTRIBUTION_BRIDGE_COOKIE,
  parseAttributionBridgeCookie,
  type AttributionBridgePayload,
} from '@/lib/seo/tracking-query-params';
import Cookies from 'js-cookie';

export type CheckoutUtmParams = {
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
};

export type CheckoutClickIds = {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  msclkid?: string;
  /** Google Ads `gad_source` when present on the landing URL. */
  gad_source?: string;
  /** Afilio affiliate click (`id_click` URL param). */
  afilio_id_click?: string;
  /** Indexa affiliate click (`indexaclickid` URL param). */
  indexaclickid?: string;
};

/** One arrival with campaign params or an external referrer. */
export type CheckoutTouch = {
  utm?: CheckoutUtmParams;
  click_ids?: CheckoutClickIds;
  referrer?: string;
  landing_page?: string;
  captured_at: string;
};

export type CheckoutClientChannel = 'browser' | 'pwa' | 'app_webview';

export type CheckoutClientMetadata = {
  utm?: CheckoutUtmParams;
  referrer?: string;
  landing_page?: string;
  checkout_page?: string;
  client_session_id?: string;
  click_ids?: CheckoutClickIds;
  first_touch_at?: string;
  last_touch?: CheckoutTouch;
  client_channel?: CheckoutClientChannel;
  shop_version?: string;
  cart_id?: string;
  cart_started_at?: string;
  cart_age_seconds?: number;
};

type StoredAttribution = {
  utm?: CheckoutUtmParams;
  referrer?: string;
  landing_page?: string;
  click_ids?: CheckoutClickIds;
  captured_at?: string;
  last_touch?: CheckoutTouch;
};

const API_HOSTNAME = (() => {
  try {
    return new URL(process.env.NEXT_PUBLIC_REST_API_ENDPOINT ?? '').hostname;
  } catch {
    return undefined;
  }
})();

/** document.referrer is fixed for the whole page load, unlike the SPA route. */
let hasCapturedPageLoadReferrer = false;

function pickParam(
  params: URLSearchParams,
  keys: string[],
): string | undefined {
  for (const key of keys) {
    const value = params.get(key);
    if (value && value.trim()) {
      return value.trim().slice(0, 255);
    }
  }
  return undefined;
}

function hasAnyValue(obj?: Record<string, string | undefined>): boolean {
  if (!obj) return false;
  return Object.values(obj).some((value) => !!value);
}

/** Social login round-trips through these hosts; they are never a traffic source. */
function isAuthRedirectHost(hostname: string): boolean {
  return (
    hostname.startsWith('accounts.google.') ||
    hostname === 'appleid.apple.com' ||
    hostname === API_HOSTNAME
  );
}

/** Returns the referrer only when it is a valid external, non-login URL. */
function toExternalReferrer(referrer?: string): string | undefined {
  if (typeof window === 'undefined' || !referrer) {
    return undefined;
  }
  try {
    const refHost = new URL(referrer).hostname;
    if (
      refHost &&
      refHost !== window.location.hostname &&
      !isAuthRedirectHost(refHost)
    ) {
      return referrer.slice(0, 2048);
    }
  } catch {
    // Invalid referrer URL — do not store
  }
  return undefined;
}

function getExternalReferrer(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  return toExternalReferrer(document.referrer);
}

/** A touch stops counting 7 days after the click, like the affiliate windows. */
const ATTRIBUTION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function isExpired(capturedAt?: string): boolean {
  if (!capturedAt) return true;
  const time = Date.parse(capturedAt);
  return !Number.isFinite(time) || Date.now() - time > ATTRIBUTION_TTL_MS;
}

/**
 * Drops touches older than the TTL. When only the first touch expired, the
 * still-valid last touch becomes the first touch of the new window.
 */
function dropExpiredTouches(stored: StoredAttribution): StoredAttribution {
  const lastTouch =
    stored.last_touch && !isExpired(stored.last_touch.captured_at)
      ? stored.last_touch
      : undefined;

  if (!isExpired(stored.captured_at)) {
    return { ...stored, last_touch: lastTouch };
  }
  if (!lastTouch) return {};
  return {
    utm: lastTouch.utm,
    click_ids: lastTouch.click_ids,
    referrer: lastTouch.referrer,
    landing_page: lastTouch.landing_page,
    captured_at: lastTouch.captured_at,
    last_touch: lastTouch,
  };
}

function readStoredAttribution(): StoredAttribution | null {
  try {
    const raw = safeLocalStorageGetItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAttribution;
    return parsed && typeof parsed === 'object'
      ? dropExpiredTouches(parsed)
      : null;
  } catch {
    return null;
  }
}

/** Starts a fresh attribution window; call once an order is placed. */
export function clearCheckoutAttribution(): void {
  safeLocalStorageRemoveItem(ATTRIBUTION_STORAGE_KEY);
}

function writeStoredAttribution(data: StoredAttribution): void {
  try {
    safeLocalStorageSetItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore quota / private mode
  }
}

function resolveClientChannel(): CheckoutClientChannel {
  if (isNativeApp()) return 'app_webview';
  if (isPWA()) return 'pwa';
  return 'browser';
}

function resolveShopVersion(): string | undefined {
  const version = process.env.NEXT_PUBLIC_VERSION?.trim();
  return version || undefined;
}

function consumeAttributionBridgeCookie(): AttributionBridgePayload | null {
  if (typeof window === 'undefined') return null;
  const raw = Cookies.get(ATTRIBUTION_BRIDGE_COOKIE);
  const payload = parseAttributionBridgeCookie(raw);
  if (raw) {
    Cookies.remove(ATTRIBUTION_BRIDGE_COOKIE, { path: '/' });
  }
  return payload;
}

function mergeParam(
  fromQuery: string | undefined,
  fromBridge: string | undefined,
): string | undefined {
  return fromQuery || fromBridge || undefined;
}

/**
 * Capture first-touch and last-touch UTM / click ids / landing page / referrer.
 * Call on app load and route changes. Only arrivals with campaign params or an
 * external referrer count. The first touch is one snapshot kept for 7 days (or
 * until an order is placed); `last_touch` is replaced on every such arrival.
 *
 * The landing URL keeps `gclid` / `utm_*`. A leftover
 * {@link ATTRIBUTION_BRIDGE_COOKIE} from an older redirect is still merged.
 */
export function captureCheckoutAttribution(url?: string): void {
  if (typeof window === 'undefined') return;

  const bridge = consumeAttributionBridgeCookie();

  const href = url
    ? new URL(url, window.location.origin).href
    : window.location.href;
  const parsed = new URL(href);
  const params = parsed.searchParams;
  const landingPage = `${parsed.pathname}${parsed.search}`.slice(0, 2048);

  const utm: CheckoutUtmParams = {
    source: mergeParam(
      pickParam(params, ['utm_source']),
      bridge?.utm_source,
    ),
    medium: mergeParam(
      pickParam(params, ['utm_medium']),
      bridge?.utm_medium,
    ),
    campaign: mergeParam(
      pickParam(params, ['utm_campaign']),
      bridge?.utm_campaign,
    ),
    term: mergeParam(pickParam(params, ['utm_term']), bridge?.utm_term),
    content: mergeParam(
      pickParam(params, ['utm_content']),
      bridge?.utm_content,
    ),
  };

  const click_ids: CheckoutClickIds = {
    gclid: mergeParam(pickParam(params, ['gclid']), bridge?.gclid),
    gbraid: mergeParam(pickParam(params, ['gbraid']), bridge?.gbraid),
    wbraid: mergeParam(pickParam(params, ['wbraid']), bridge?.wbraid),
    fbclid: mergeParam(pickParam(params, ['fbclid']), bridge?.fbclid),
    msclkid: mergeParam(pickParam(params, ['msclkid']), bridge?.msclkid),
    gad_source: mergeParam(
      pickParam(params, ['gad_source']),
      bridge?.gad_source,
    ),
    afilio_id_click: pickParam(params, ['id_click']),
    indexaclickid: pickParam(params, ['indexaclickid']),
  };

  const pageLoadReferrer = hasCapturedPageLoadReferrer
    ? undefined
    : getExternalReferrer();
  hasCapturedPageLoadReferrer = true;

  const existing = readStoredAttribution() ?? {};
  const next: StoredAttribution = { ...existing };

  // Login hosts saved by older builds are never a source
  if (existing.referrer && !toExternalReferrer(existing.referrer)) {
    next.referrer = undefined;
  }

  if (hasAnyValue(utm) || hasAnyValue(click_ids) || pageLoadReferrer) {
    const touch: CheckoutTouch = {
      utm: hasAnyValue(utm) ? utm : undefined,
      click_ids: hasAnyValue(click_ids) ? click_ids : undefined,
      referrer: pageLoadReferrer,
      landing_page: landingPage,
      captured_at: new Date().toISOString(),
    };
    if (!existing.captured_at) {
      next.utm = touch.utm;
      next.click_ids = touch.click_ids;
      next.referrer = touch.referrer;
      next.landing_page = touch.landing_page;
      next.captured_at = touch.captured_at;
    }
    next.last_touch = touch;
  }

  writeStoredAttribution(next);
}

/**
 * Build the client payload to attach on order create.
 */
export function getCheckoutClientMetadata(): CheckoutClientMetadata {
  if (typeof window === 'undefined') {
    return {};
  }

  const stored = readStoredAttribution() ?? {};
  const cartSession = ensureCartSession();
  const cartAgeSeconds = getCartAgeSeconds();
  const metadata: CheckoutClientMetadata = {
    client_session_id: getOrCreateSessionId() || undefined,
    checkout_page: `${window.location.pathname}${window.location.search}`.slice(
      0,
      2048,
    ),
    client_channel: resolveClientChannel(),
    shop_version: resolveShopVersion(),
  };

  if (hasAnyValue(stored.utm)) {
    metadata.utm = stored.utm;
  }
  if (hasAnyValue(stored.click_ids)) {
    metadata.click_ids = stored.click_ids;
  }
  if (stored.landing_page) {
    metadata.landing_page = stored.landing_page;
  }
  const referrer = toExternalReferrer(stored.referrer) ?? getExternalReferrer();
  if (referrer) {
    metadata.referrer = referrer;
  }
  if (stored.captured_at) {
    metadata.first_touch_at = stored.captured_at;
  }
  if (stored.last_touch) {
    metadata.last_touch = stored.last_touch;
  }

  if (cartSession) {
    metadata.cart_id = cartSession.cart_id;
    metadata.cart_started_at = cartSession.started_at;
  }
  if (typeof cartAgeSeconds === 'number') {
    metadata.cart_age_seconds = cartAgeSeconds;
  }

  return metadata;
}
